import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ExternalLink, RefreshCw, X } from 'lucide-react';
import {
  apiMessage,
  getDigest,
  getDigestEmails,
  getTaskStatuses,
  locateMessage,
} from '../services/serviceInboxApi';
import { catColor, catLabel } from './categories';
import './ClassificationReview.css'; // the --cat-* palette, via .cat-palette

/**
 * OpsDigest — what came into the service inbox over a day or a week, and what
 * happened to it. SOW deliverable #9: volume by category, handled automatically
 * versus flagged, average confidence, and the ServiceTitan tasks behind it.
 *
 * Counted by when the mail was received, in the reader's own time zone: the
 * page sends the period's bounds, the service inbox counts (/api/inbox/digest).
 * "Flagged" means filed to Needs Review or Processing Error; every other folder,
 * an archive or a delete was handled with nobody.
 *
 * Whether a created task is still open lives in ServiceTitan, so those counts are
 * read live from the platform (one call per office), after the rest has drawn.
 *
 * Part of the service_inbox feature, like the Mailbox. Read-only.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

// Midnight at the start of the period holding `d`: the day, or the Monday of its week.
const periodStart = (d, period) => {
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (period === 'week') s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
  return s;
};

const shift = (start, period, n) => {
  const s = new Date(start);
  s.setDate(s.getDate() + n * (period === 'week' ? 7 : 1));
  return s;
};

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const fmtDay = (d, opts = {}) =>
  d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', ...opts });

const periodLabel = (start, until, period) => {
  if (period === 'day') return fmtDay(start, { year: 'numeric' });
  const last = new Date(until.getTime() - DAY_MS);
  return `${fmtDay(start)} – ${fmtDay(last, { year: 'numeric' })}`;
};

const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);

const LOOKUP_ROWS = [
  ['matched', 'Matched one customer'],
  ['ambiguous', 'More than one customer'],
  ['no_match', 'No customer found'],
  ['insufficient_signals', 'Not enough to search on'],
  ['error', 'Lookup failed'],
];

const TASK_ROWS = [
  ['created', 'Created'],
  ['pending', 'Being created'],
  ['skipped', 'Skipped (routing or customer)'],
  ['error', 'Write failed'],
  ['not_attempted', 'Not attempted'],
];

const FORWARD_ROWS = [
  ['sent', 'Sent'],
  ['failed', 'Refused'],
  ['unknown', 'Outcome unknown'],
  ['claimed', 'Interrupted'],
];

// One headline number.
const Tile = ({ label, value, note, tone = 'text-gray-900' }) => (
  <div className="rounded-md border border-gray-200 bg-white px-4 py-3">
    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
    {note && <div className="mt-0.5 text-xs text-gray-500">{note}</div>}
  </div>
);

const Panel = ({ title, children, className = '' }) => (
  <section className={`rounded-md border border-gray-200 bg-white ${className}`}>
    <h2 className="border-b border-gray-200 px-4 py-2 text-sm font-semibold text-gray-800">{title}</h2>
    <div className="px-4 py-3">{children}</div>
  </section>
);

// Label and count rows, with nothing drawn for a zero unless `keepZero`.
const Counts = ({ rows, empty = 'None.' }) => {
  const shown = rows.filter(([, n, keep]) => n || keep);
  if (!shown.length) return <div className="text-sm text-gray-500">{empty}</div>;
  return (
    <dl className="space-y-1 text-sm">
      {shown.map(([label, n, , note]) => (
        <div key={label} className="flex items-baseline justify-between gap-3">
          <dt className="text-gray-700">
            {label}
            {note && <span className="ml-1 text-xs text-gray-500">{note}</span>}
          </dt>
          <dd className="tabular-nums font-medium text-gray-900">{n}</dd>
        </div>
      ))}
    </dl>
  );
};

// Volume by category: one bar per category, one hue (the bars show size, and
// the dot beside each name is the category colour the rest of the app uses).
// A category's name lists its emails below the chart.
const CategoryBars = ({ categories, total, threshold, selected, onSelect }) => {
  const max = Math.max(1, ...categories.map((c) => c.count));
  return (
    <div className="space-y-2">
      {categories.map((c) => (
        <div
          key={c.category || 'none'}
          className="grid grid-cols-[minmax(7rem,10rem)_1fr_auto] items-center gap-3 text-sm"
          title={`${catLabel(c.category)}: ${c.count} of ${total} (${pct(c.count, total)}%)${
            c.avg_confidence != null ? `, average confidence ${c.avg_confidence}%` : ''
          }`}
        >
          <span className="flex min-w-0 items-center gap-1.5 text-gray-800">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: catColor(c.category) }} />
            <button
              type="button"
              onClick={() => onSelect(c.category || 'unclassified')}
              aria-pressed={selected === (c.category || 'unclassified')}
              title="List these emails"
              className={`truncate text-left hover:underline ${
                selected === (c.category || 'unclassified') ? 'font-medium text-blue-800' : 'text-blue-700'
              }`}
            >
              {catLabel(c.category)}
            </button>
          </span>
          <span className="h-2.5 rounded-r bg-gray-100">
            <span
              className="block h-2.5 rounded-r bg-blue-600"
              style={{ width: `${Math.max(2, (c.count / max) * 100)}%` }}
            />
          </span>
          <span className="w-28 text-right tabular-nums text-gray-700">
            <span className="font-medium text-gray-900">{c.count}</span>
            <span className="text-gray-500"> · {pct(c.count, total)}%</span>
            {c.avg_confidence != null && (
              <span
                className={`ml-2 text-xs ${c.avg_confidence < threshold ? 'text-amber-700' : 'text-gray-500'}`}
              >
                {Math.round(c.avg_confidence)}%
              </span>
            )}
          </span>
        </div>
      ))}
      <div className="pt-1 text-xs text-gray-500">
        Count · share of mail · average confidence (amber below {threshold}%).
      </div>
    </div>
  );
};

const isClosed = (status) => status === 'Completed' || status === 'Canceled';

// A zero drawn quietly, so the numbers that matter stand out in a table.
// A cell is a count, or `{ n, of }` drawn "n/of" (matched of all lookups).
const Num = ({ n, strong = false }) => {
  if (n && typeof n === 'object') {
    return n.of ? (
      <span className="tabular-nums text-gray-800">
        <span className="font-medium text-gray-900">{n.n}</span>
        <span className="text-gray-500">/{n.of}</span>
      </span>
    ) : (
      <span className="text-gray-300">–</span>
    );
  }
  return n ? (
    <span className={`tabular-nums ${strong ? 'font-medium text-gray-900' : 'text-gray-800'}`}>{n}</span>
  ) : (
    <span className="text-gray-300">–</span>
  );
};

const addCells = (a, b) =>
  b && typeof b === 'object'
    ? { n: (a?.n || 0) + (b.n || 0), of: (a?.of || 0) + (b.of || 0) }
    : (a || 0) + (b || 0);

// Rows of counts under a set of columns, with a total row. `rows` is
// [{ key, label, dot?, cells: [n, ...] }]; the last column is a total unless
// `noTotalColumn`.
const Breakdown = ({ columns, rows, first = 'Category', empty }) => {
  if (!rows.length) return <div className="text-sm text-gray-500">{empty}</div>;
  const totals = columns.map((_, i) => rows.reduce((n, r) => addCells(n, r.cells[i]), 0));
  return (
    <div className="-mx-4 overflow-x-auto px-4">
      <table className="w-full min-w-[32rem] text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs text-gray-500">
            <th className="py-1.5 pr-3 text-left font-medium">{first}</th>
            {columns.map((c) => (
              <th key={c} className="px-2 py-1.5 text-right font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-gray-100 last:border-0">
              <td className="py-1.5 pr-3 text-gray-800">
                <span className="flex items-center gap-1.5">
                  {r.dot && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: r.dot }} />}
                  {r.label}
                </span>
              </td>
              {r.cells.map((n, i) => (
                <td key={columns[i]} className="px-2 py-1.5 text-right">
                  <Num n={n} strong={columns[i] === 'Total'} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {rows.length > 1 && (
          <tfoot>
            <tr className="border-t border-gray-200 text-xs font-medium text-gray-600">
              <td className="py-1.5 pr-3">Total</td>
              {totals.map((n, i) => (
                <td key={columns[i]} className="px-2 py-1.5 text-right tabular-nums">
                  {n && typeof n === 'object' ? (n.of ? `${n.n}/${n.of}` : '–') : n || '–'}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

// Categories in the order the bars draw them (largest first), then any others.
const categoryOrder = (data, keys) => {
  const order = data.categories.map((c) => c.category || '');
  return [...keys].sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });
};

const officeName = (data, tenant) => (tenant ? data.tenant_labels?.[tenant] || tenant : 'No office');

// Customer lookup: each category's outcomes, then matched customers by office.
const LookupDetail = ({ data }) => {
  const byCat = data.lookups_by_category || {};
  // Matched is shown as matched of all lookups in the category ("2/4"); the
  // rest of that total is the columns beside it plus "no customer found".
  const rows = categoryOrder(data, Object.keys(byCat)).map((cat) => {
    const m = byCat[cat];
    const all = Object.values(m).reduce((a, b) => a + b, 0);
    return {
      key: cat,
      label: catLabel(cat),
      dot: catColor(cat),
      cells: [
        { n: m.matched || 0, of: all },
        m.ambiguous || 0,
        m.insufficient_signals || 0,
        m.error || 0,
      ],
    };
  });
  const offices = Object.entries(data.matched_by_tenant || {}).sort((a, b) => b[1] - a[1]);
  return (
    <>
      <Breakdown
        columns={['Matched', 'More than one', 'Not enough info', 'Failed']}
        rows={rows}
        empty="No lookups."
      />
      {offices.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-xs font-medium text-gray-500">Matched customers by office</div>
          <div className="flex flex-wrap gap-1.5">
            {offices.map(([tenant, n]) => (
              <span
                key={tenant}
                className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5 text-sm text-gray-800"
              >
                {officeName(data, tenant)}
                <span className="tabular-nums font-medium text-gray-900">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ServiceTitan tasks: one row per office and category ("Orlando West · General"),
// only where a task was created or failed. Open and closed are read live.
const TaskDetail = ({ data, tasks }) => {
  const live = !tasks.loading && !tasks.error;
  const pairs = {};
  (data.tasks_by_office_category || []).forEach(({ tenant_id: t, category: c, status, count }) => {
    const key = `${t}|${c}`;
    pairs[key] = pairs[key] || { tenant: t, category: c };
    pairs[key][status] = (pairs[key][status] || 0) + count;
  });
  const liveCount = (p, closed) =>
    (data.created_tasks || []).filter((t) => {
      const st = tasks.byId[String(t.task_id)];
      return (
        (t.tenant_id || '') === p.tenant &&
        (t.category || '') === p.category &&
        st &&
        isClosed(st) === closed
      );
    }).length;

  const rows = Object.values(pairs)
    .filter((p) => (p.created || 0) + (p.error || 0) > 0)
    .sort(
      (a, b) =>
        officeName(data, a.tenant).localeCompare(officeName(data, b.tenant)) ||
        (b.created || 0) - (a.created || 0) ||
        catLabel(a.category).localeCompare(catLabel(b.category))
    )
    .map((p) => {
      const created = p.created || 0;
      return {
        key: `${p.tenant}|${p.category}`,
        label: `${officeName(data, p.tenant)} · ${catLabel(p.category)}`,
        dot: catColor(p.category),
        cells: [
          created,
          live && created ? liveCount(p, false) : 0,
          live && created ? liveCount(p, true) : 0,
          p.error || 0,
        ],
      };
    });

  return (
    <>
      <Breakdown
        first="Office · Category"
        columns={['Created', 'Open', 'Closed', 'Failed']}
        rows={rows}
        empty="No tasks created or failed."
      />
      <div className="mt-2 text-xs text-gray-500">
        {tasks.loading && 'Reading open and closed from ServiceTitan… '}
        {tasks.error && <span className="text-red-700">{tasks.error} </span>}
        Only offices and categories with a created or failed task are listed; skipped tasks are counted
        in the summary above. Open and closed are read live from ServiceTitan.
      </div>
    </>
  );
};

// Open and closed, read live from ServiceTitan for the tasks created this period.
const useTaskStatuses = (created) => {
  const [state, setState] = useState({ loading: false, open: 0, closed: 0, missing: 0, byId: {}, error: null });
  useEffect(() => {
    if (!created?.length) {
      setState({ loading: false, open: 0, closed: 0, missing: 0, byId: {}, error: null });
      return undefined;
    }
    let live = true;
    const byTenant = {};
    created.forEach(({ task_id: id, tenant_id: t }) => {
      if (t && /^\d+$/.test(String(id))) (byTenant[t] = byTenant[t] || []).push(id);
    });
    const calls = Object.entries(byTenant).flatMap(([t, ids]) => {
      const chunks = [];
      for (let i = 0; i < ids.length; i += 50) chunks.push(getTaskStatuses(t, ids.slice(i, i + 50)));
      return chunks;
    });
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.all(calls)
      .then((answers) => {
        if (!live) return;
        const tasks = answers.flatMap((a) => a.tasks || []);
        const closed = tasks.filter((t) => isClosed(t.status)).length;
        setState({
          loading: false,
          open: tasks.length - closed,
          closed,
          byId: Object.fromEntries(tasks.map((t) => [String(t.task_id), t.status])),
          missing: answers.reduce((n, a) => n + (a.missing?.length || 0), 0),
          error: null,
        });
      })
      .catch((err) => {
        if (live) setState((s) => ({ ...s, loading: false, error: apiMessage(err, 'Could not read task status from ServiceTitan.') }));
      });
    return () => {
      live = false;
    };
  }, [created]);
  return state;
};

const LOOKUP_LABELS = Object.fromEntries(LOOKUP_ROWS);
const TASK_LABELS = Object.fromEntries(TASK_ROWS);

const fmtTime = (iso, spansDays) =>
  iso
    ? new Date(iso).toLocaleString(
        undefined,
        spansDays
          ? { weekday: 'short', hour: 'numeric', minute: '2-digit' }
          : { hour: 'numeric', minute: '2-digit' }
      )
    : '';

// Opens one email in the Mailbox wherever it is now. The Graph id stored with it
// changes each time someone moves the message; its Message-ID does not.
const OpenInMailbox = ({ internetId }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  if (!internetId) return <span className="text-xs text-gray-400">no id</span>;
  const open = async () => {
    setState('finding');
    try {
      const where = await locateMessage(internetId);
      const params = new URLSearchParams({ message: where.id });
      if (where.folder_id) params.set('folder', where.folder_id);
      navigate(`/mailbox?${params}`);
    } catch (err) {
      setState(err?.response?.status === 404 ? 'gone' : 'error');
    }
  };
  if (state === 'gone') return <span className="text-xs text-gray-500">no longer in the mailbox</span>;
  return (
    <button
      type="button"
      onClick={open}
      disabled={state === 'finding'}
      className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline disabled:text-gray-400"
      title={state === 'error' ? 'Could not reach the mailbox; try again' : 'Open in the Mailbox'}
    >
      {state === 'finding' ? 'Finding…' : state === 'error' ? 'Retry' : 'Open'}
      <ExternalLink className="h-3 w-3" />
    </button>
  );
};

// The emails behind one category's count, from the service inbox's own records,
// so the list always adds up to the number on the chart.
const CategoryEmails = ({ category, start, until, onClose }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let live = true;
    getDigestEmails(start, until, category)
      .then((data) => live && setState({ loading: false, error: null, data }))
      .catch(
        (err) =>
          live &&
          setState({ loading: false, error: apiMessage(err, 'Could not list the emails.'), data: null })
      );
    return () => {
      live = false;
    };
  }, [category, start, until]);
  const spansDays = until - start > DAY_MS;
  const emails = state.data?.emails || [];
  const label = category === 'unclassified' ? 'Unclassified' : catLabel(category);
  return (
    <section className="mt-4 rounded-md border border-blue-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2">
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ background: catColor(category === 'unclassified' ? null : category) }}
        />
        <h2 className="text-sm font-semibold text-gray-800">
          {label}
          {state.data && (
            <span className="font-normal text-gray-500">
              {' '}
              · {state.data.total} email{state.data.total === 1 ? '' : 's'}
              {state.data.truncated ? `, newest ${emails.length} shown` : ''}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          aria-label="Close the list"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="px-4 py-3">
        {state.loading && <div className="text-sm text-gray-500">Loading…</div>}
        {state.error && <div className="text-sm text-red-700">{state.error}</div>}
        {state.data && !emails.length && <div className="text-sm text-gray-500">No emails.</div>}
        {emails.length > 0 && (
          <div className="-mx-4 overflow-x-auto px-4">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                  <th className="py-1.5 pr-3 font-medium">Received</th>
                  <th className="py-1.5 pr-3 font-medium">From</th>
                  <th className="py-1.5 pr-3 font-medium">Subject</th>
                  <th className="py-1.5 pr-3 font-medium">Filed to</th>
                  <th className="py-1.5 pr-3 font-medium">Customer</th>
                  <th className="py-1.5 pr-3 font-medium">Task</th>
                  <th className="py-1.5 font-medium" />
                </tr>
              </thead>
              <tbody>
                {emails.map((e) => (
                  <tr key={e.email_id} className="border-b border-gray-100 align-top last:border-0">
                    <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums text-gray-600">
                      {fmtTime(e.received_at, spansDays)}
                    </td>
                    <td className="max-w-[12rem] truncate py-1.5 pr-3 text-gray-800" title={e.sender_address}>
                      {e.sender_name || e.sender_address}
                    </td>
                    <td className="max-w-[24rem] py-1.5 pr-3 text-gray-900" title={e.summary || undefined}>
                      <div className="truncate">{e.subject || '(no subject)'}</div>
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-gray-600">{e.filed_to || '—'}</td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-gray-600">
                      {e.lookup ? LOOKUP_LABELS[e.lookup] || e.lookup : '—'}
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-gray-600">
                      {e.task ? TASK_LABELS[e.task] || e.task : '—'}
                    </td>
                    <td className="whitespace-nowrap py-1.5 text-right">
                      <OpenInMailbox internetId={e.internet_message_id} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-2 text-xs text-gray-500">
          From the service inbox&apos;s records. &quot;Filed to&quot; is where the automation put it;
          Open finds the message wherever it is now.
        </div>
      </div>
    </section>
  );
};

// The day the reader picked is kept as its own thing, not replaced by the start
// of its period: the week view shows the week holding it, and switching back to
// Day returns to that same day rather than to the week's Monday.
export const digestPeriod = (dateParam, periodParam, now = new Date()) => {
  const period = periodParam === 'week' ? 'week' : 'day';
  const today = periodStart(now, 'day');
  let day = parseYmd(dateParam) || today;
  if (day > today) day = today;
  const start = periodStart(day, period);
  const until = shift(start, period, 1);
  return { period, day, today, start, until, isCurrent: until > now };
};

const OpsDigest = () => {
  const [params, setParams] = useSearchParams();
  const { period, day, today, start, until, isCurrent } = useMemo(
    () => digestPeriod(params.get('date'), params.get('period')),
    [params]
  );

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // The category whose emails are listed under the chart, if any.
  const [selected, setSelected] = useState(null);

  const go = (next) => {
    const date = next.date || day;
    setParams({ period: next.period || period, date: ymd(date > today ? today : date) }, { replace: true });
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getDigest(start, until)
      .then(setData)
      .catch((err) => setError(apiMessage(err, 'Could not load the digest.')))
      .finally(() => setLoading(false));
  }, [start, until]);

  useEffect(() => {
    load();
  }, [load]);

  const tasks = useTaskStatuses(data?.created_tasks);

  const total = data?.total ?? 0;
  const threshold = data?.confidence_threshold ?? 80;
  const created = data?.tasks?.created ?? 0;

  let taskNote = 'none created';
  if (created) {
    if (tasks.loading) taskNote = 'reading status from ServiceTitan…';
    else if (tasks.error) taskNote = 'status unavailable';
    else taskNote = `${tasks.open} open · ${tasks.closed} closed${tasks.missing ? ` · ${tasks.missing} not found` : ''}`;
  }

  // The task breakdown only earns its space when a task was made or failed: a
  // period of nothing but skips says no more than the summary panel.
  const showTaskDetail = Boolean(
    data && ['created', 'error'].some((k) => (data.tasks?.[k] || 0) > 0)
  );

  const folderRows = data
    ? [
        ...Object.entries(data.filed)
          .sort((a, b) => b[1] - a[1])
          .map(([folder, n]) => [folder, n]),
        ['Archived', data.archived],
        ['Deleted', data.deleted],
        ['Not filed yet', data.not_filed, false, 'waiting on a retry'],
      ]
    : [];

  return (
    <div className="cat-palette">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Ops Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Service inbox mail received {period === 'day' ? 'on' : 'in the week of'}{' '}
            <span className="font-medium">{periodLabel(start, until, period)}</span>, and what happened to it.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5 text-sm">
            {['day', 'week'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => go({ period: p })}
                aria-pressed={period === p}
                className={`rounded px-3 py-1 ${period === p ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                {p === 'day' ? 'Day' : 'Week'}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center rounded-md border border-gray-300 bg-white">
            <button
              type="button"
              onClick={() => go({ date: shift(day, period, -1) })}
              className="rounded-l-md p-1.5 text-gray-600 hover:bg-gray-100"
              aria-label={period === 'day' ? 'Previous day' : 'Previous week'}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => go({ date: today })}
              disabled={isCurrent}
              className="border-x border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:text-gray-400 disabled:hover:bg-white"
            >
              {period === 'day' ? 'Today' : 'This week'}
            </button>
            <button
              type="button"
              onClick={() => go({ date: shift(day, period, 1) })}
              disabled={isCurrent}
              className="rounded-r-md p-1.5 text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-white"
              aria-label={period === 'day' ? 'Next day' : 'Next week'}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <input
            type="date"
            value={ymd(day)}
            max={ymd(today)}
            onChange={(e) => {
              const picked = parseYmd(e.target.value);
              if (picked) go({ date: picked });
            }}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700"
            aria-label="Pick a day"
          />
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {!data && loading && <div className="text-sm text-gray-500">Loading…</div>}

      {data && (
        <div className={loading ? 'opacity-60 transition-opacity' : ''}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Tile label="Emails received" value={total} note={isCurrent ? 'so far' : undefined} />
            <Tile
              label="Handled automatically"
              value={data.handled_automatically}
              note={`${pct(data.handled_automatically, total)}% · filed with nobody`}
            />
            <Tile
              label="Flagged for review"
              value={data.flagged}
              note={`${pct(data.flagged, total)}% · ${data.below_threshold} below ${threshold}% confidence`}
            />
            <Tile
              label="Average confidence"
              value={data.avg_confidence != null ? `${Math.round(data.avg_confidence)}%` : '—'}
              tone={data.avg_confidence != null && data.avg_confidence < threshold ? 'text-amber-700' : 'text-gray-900'}
            />
            <Tile label="ServiceTitan tasks" value={created} note={taskNote} />
          </div>

          {total === 0 ? (
            <div className="mt-4 rounded-md border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
              No mail received {period === 'day' ? 'this day' : 'this week'}.
            </div>
          ) : (
            <>
              <Panel title="By category" className="mt-4">
                <CategoryBars
                  categories={data.categories}
                  total={total}
                  threshold={threshold}
                  selected={selected}
                  onSelect={(cat) => setSelected((cur) => (cur === cat ? null : cat))}
                />
              </Panel>
              {selected && (
                <CategoryEmails
                  key={`${selected}|${start.getTime()}|${until.getTime()}`}
                  category={selected}
                  start={start}
                  until={until}
                  onClose={() => setSelected(null)}
                />
              )}

              <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Panel title="Where it was filed">
                  <Counts rows={folderRows} />
                </Panel>
                <Panel title="Customer lookup">
                  <Counts
                    rows={LOOKUP_ROWS.map(([k, label]) => [label, data.lookups[k] || 0])}
                    empty="No lookups."
                  />
                </Panel>
                <Panel title="ServiceTitan tasks">
                  <Counts
                    rows={TASK_ROWS.map(([k, label]) => [
                      label,
                      data.tasks[k] || 0,
                      false,
                      k === 'created' && created && !tasks.loading && !tasks.error
                        ? `${tasks.open} open`
                        : undefined,
                    ])}
                    empty="No task decisions."
                  />
                  {tasks.error && <div className="mt-2 text-xs text-red-700">{tasks.error}</div>}
                </Panel>
                <Panel title="Forwards">
                  <Counts
                    rows={FORWARD_ROWS.map(([k, label]) => [label, data.forwards[k] || 0])}
                    empty="Nothing forwarded."
                  />
                </Panel>
              </div>

              <div className={`mt-4 grid gap-4 ${showTaskDetail ? '2xl:grid-cols-2' : ''}`}>
                <Panel title="Customer lookup by category">
                  <LookupDetail data={data} />
                </Panel>
                {showTaskDetail && (
                  <Panel title="ServiceTitan tasks by office and category">
                    <TaskDetail data={data} tasks={tasks} />
                  </Panel>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OpsDigest;
